import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface FeatureImportance {
  feature: string;
  importance: number;
  impact: 'positive' | 'negative';
}

interface FeatureAttributionChartProps {
  data: FeatureImportance[];
  loading?: boolean;
}

const FeatureAttributionChart: React.FC<FeatureAttributionChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const chartData = useMemo(() => 
    data.map(item => ({
      feature: item.feature.replace(/_/g, ' '),
      importance: Math.round(item.importance * 1000) / 10,
      impact: item.impact
    })), [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-xl p-4 shadow-xl backdrop-blur-sm">
          <p className="text-slate-200 font-semibold mb-2">{label}</p>
          <p className="text-violet-400 font-bold">Importance: {data.importance}%</p>
          <p className={`font-bold ${data.impact === 'positive' ? 'text-green-400' : 'text-red-400'}`}>
            Impact: {data.impact}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Progress Bars */}
      <div className="space-y-3">
        {chartData.map((feature, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700 w-32">{feature.feature}</span>
            <div className="flex items-center gap-3 flex-1">
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    feature.impact === 'positive' ? 'bg-green-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${feature.importance}%` }}
                ></div>
              </div>
              <span className="text-sm font-bold text-slate-600 w-12">{feature.importance}%</span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Bar Chart */}
      <div className="w-full h-32 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="horizontal">
            <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
            <XAxis 
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#64748b' }}
            />
            <YAxis 
              type="category"
              dataKey="feature"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#64748b' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="importance" 
              fill="#6366f1"
              radius={[0, 2, 2, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FeatureAttributionChart;